import { useCallback, useEffect, useMemo, useState } from 'react';
import { getScaleConsumerToken } from '../api/scaleApi';
import { startScaleEvents, stopScaleEvents, subscribeScaleEvents } from '../utils/scaleEventsHub';

export const SCALE_READING_FRESHNESS_MS = 8000;

const EMPTY_SCALE_STATE = {
  enabled: false,
  connectionStatus: 'idle',
  status: 'unpaired',
  binding: null,
  registeredDevice: null,
  transmissionMode: null,
  latestStableWeightKg: null,
  latestStableAt: null,
  lastSourcePort: null,
  lastMessageType: null,
  lastError: null,
};

const normalizeBindingStableWeight = (binding) => {
  const weight = Number(binding?.lastStableWeightKg);
  return Number.isFinite(weight) ? weight : null;
};

const normalizeBindingStableAt = (binding) => {
  const value = binding?.lastStableAt ? new Date(binding.lastStableAt).toISOString() : null;
  return value && !Number.isNaN(new Date(value).getTime()) ? value : null;
};

const normalizeTransmissionMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'stable_non_zero') return 'stable_non_zero';
  if (normalized === 'continuous') return 'continuous';
  return null;
};

const resolveTransmissionMode = (event, previousMode) => (
  Object.prototype.hasOwnProperty.call(event || {}, 'transmissionMode')
    ? normalizeTransmissionMode(event?.transmissionMode)
    : previousMode
);

const isFreshStableReading = (stableAt, freshnessMs = SCALE_READING_FRESHNESS_MS) => {
  if (!stableAt) return false;
  const parsed = new Date(stableAt).getTime();
  if (Number.isNaN(parsed)) return false;
  return (Date.now() - parsed) <= freshnessMs;
};

const buildScopeKey = ({ branchId, cashRegisterId, cashRegisterSessionId }) =>
  [branchId, cashRegisterId, cashRegisterSessionId].map((value) => String(value || '').trim()).join(':');

const useScaleStream = ({
  branchId,
  cashRegisterId,
  cashRegisterSessionId,
  enabled,
}) => {
  const [state, setState] = useState(EMPTY_SCALE_STATE);

  const scopeKey = useMemo(() => {
    if (!enabled) return '';
    return buildScopeKey({ branchId, cashRegisterId, cashRegisterSessionId });
  }, [branchId, cashRegisterId, cashRegisterSessionId, enabled]);

  const getConnectionConfig = useCallback(async () => {
    const response = await getScaleConsumerToken(branchId, cashRegisterId);
    if (
      cashRegisterSessionId
      && response?.cashRegisterSessionId
      && String(response.cashRegisterSessionId) !== String(cashRegisterSessionId)
    ) {
      throw new Error('La sesion de caja cambio antes de conectar la balanza.');
    }
    return response;
  }, [branchId, cashRegisterId, cashRegisterSessionId]);

  useEffect(() => {
    if (!enabled || !scopeKey) {
      stopScaleEvents();
      setState(EMPTY_SCALE_STATE);
      return undefined;
    }

    setState((prev) => ({
      ...prev,
      enabled: true,
      connectionStatus: prev.connectionStatus === 'connected' ? prev.connectionStatus : 'connecting',
      lastError: null,
    }));

    startScaleEvents({ scopeKey, getConnectionConfig });
    return () => {
      stopScaleEvents();
    };
  }, [enabled, getConnectionConfig, scopeKey]);

  useEffect(() => subscribeScaleEvents((event) => {
    if (!event?.type) return;

    if (event.type === 'scale.connection') {
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        connectionStatus: event.connectionStatus || 'idle',
        lastError: event.errorMessage || (event.connectionStatus === 'error' ? prev.lastError : null),
        lastMessageType: event.type,
      }));
      return;
    }

    if (event.type === 'scale.bootstrap' || event.type === 'scale.snapshot' || event.type === 'scale.status') {
      const binding = event.binding || null;
      setState((prev) => {
        const nextRegisteredDevice = event.registeredDevice !== undefined
          ? event.registeredDevice
          : (
            event.type === 'scale.status'
            && event.status === 'unpaired'
            && event.reason !== 'session_closed'
              ? null
              : prev.registeredDevice
          );
        return {
          ...prev,
          enabled: enabled && !!scopeKey,
          status: event.status || binding?.status || 'unpaired',
          binding,
          registeredDevice: nextRegisteredDevice,
          transmissionMode: resolveTransmissionMode(event, prev.transmissionMode),
          latestStableWeightKg: normalizeBindingStableWeight(binding),
          latestStableAt: normalizeBindingStableAt(binding),
          lastSourcePort: binding?.lastSourcePort || prev.lastSourcePort || null,
          lastMessageType: event.type,
          lastError: event.reason && event.status === 'unpaired' ? event.reason : prev.lastError,
        };
      });
      return;
    }

    if (event.type === 'scale.stable_weight') {
      const binding = event.binding || null;
      const stableAt = event.stableAt || event.measuredAt || normalizeBindingStableAt(binding);
      const weightKg = Number(event.weightKg);
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        status: binding?.status || 'connected',
        binding,
        registeredDevice: prev.registeredDevice,
        transmissionMode: resolveTransmissionMode(event, prev.transmissionMode),
        latestStableWeightKg: Number.isFinite(weightKg) ? weightKg : normalizeBindingStableWeight(binding),
        latestStableAt: stableAt || null,
        lastSourcePort: event.sourcePort || binding?.lastSourcePort || null,
        lastMessageType: event.type,
        lastError: null,
      }));
      return;
    }

    if (event.type === 'scale.cleared') {
      const binding = event.binding || null;
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        status: binding?.status || prev.status || 'connected',
        binding,
        registeredDevice: prev.registeredDevice,
        transmissionMode: resolveTransmissionMode(event, prev.transmissionMode),
        latestStableWeightKg: null,
        latestStableAt: null,
        lastSourcePort: event.sourcePort || binding?.lastSourcePort || prev.lastSourcePort || null,
        lastMessageType: event.type,
      }));
    }
  }), [enabled, scopeKey]);

  return useMemo(() => {
    const hasFreshStableWeight = (
      state.latestStableWeightKg != null
      && state.latestStableWeightKg > 0
      && isFreshStableReading(state.latestStableAt)
    );
    const normalizedStatus = String(state.status || '').trim().toLowerCase();
    const hasLiveScaleStream = (
      Boolean(state.binding?.id)
      && ['paired', 'connected'].includes(normalizedStatus)
    );
    const scaleReadingStrategy = state.transmissionMode === 'stable_non_zero'
      ? 'direct_stable'
      : state.transmissionMode === 'continuous'
        ? 'app_stabilized'
        : null;

    return {
      ...state,
      hasAssignedScaleDevice: Boolean(state.registeredDevice),
      hasActiveScaleBinding: Boolean(state.binding?.id),
      hasLiveScaleStream,
      hasFreshStableWeight,
      freshStableWeightKg: hasFreshStableWeight ? state.latestStableWeightKg : null,
      freshStableAt: hasFreshStableWeight ? state.latestStableAt : null,
      scaleReadingStrategy,
      freshnessMs: SCALE_READING_FRESHNESS_MS,
    };
  }, [state]);
};

export default useScaleStream;
